/**
 * Decision 0160 (the workspaces step): `index.html` asks for the session and the workspace list in a few lines of inline
 * script, before the app's JavaScript is downloaded and parsed (on a phone after a deploy, the entry chunk and its
 * preloads are most of the boot). `lib/queries.ts` takes those answers here, once each, instead of asking again.
 *
 * The inline script (keep it in step with {@link GUEST_PAGE}) sets `window.__mingaBoot`:
 *   `{ at, session: Promise<Response>, workspaces?: Promise<Response> }` (no workspace list on a guest page).
 * Nothing here throws; without the inline script (tests, the dev fixtures) everything is asked as before.
 */

export interface HtmlBoot {
  /** `Date.now()` when the requests were sent. */
  readonly at: number
  readonly session?: Promise<Response>
  readonly workspaces?: Promise<Response>
}

/** Decision 0160: a project page's snapshot, asked for by the inline script with the project it is for. */
export interface HtmlSnapshot {
  readonly workspaceId: string
  readonly projectId: string
  readonly response: Promise<Response>
}

declare global {
  interface Window {
    __mingaBoot?: {
      at: number
      session?: Promise<Response>
      workspaces?: Promise<Response>
      snapshot?: HtmlSnapshot
    }
  }
}

/** Pages a signed-out person uses: no workspace list is asked for there (the inline script uses the same pattern). */
export const GUEST_PAGE = /^\/(?:sign-in|sign-up|forgot-password|reset-password|invite\/|oauth\/)/

const boot = (): Window["__mingaBoot"] => (typeof window === "undefined" ? undefined : window.__mingaBoot)

/** The early session response, once (null: none, or already taken). */
export const takeHtmlSession = (): { readonly at: number; readonly response: Promise<Response> } | null => {
  const b = boot()
  const response = b?.session
  if (!b || !response) return null
  delete b.session
  return { at: b.at, response }
}

/** The early workspace-list response, once (null: none, or already taken). */
export const takeHtmlWorkspaces = (): {
  readonly at: number
  readonly response: Promise<Response>
} | null => {
  const b = boot()
  const response = b?.workspaces
  if (!b || !response) return null
  delete b.workspaces
  return { at: b.at, response }
}

/**
 * Decision 0160: the early snapshot response for this project, once (null: none, another project's, or already taken;
 * another project's is dropped, so a later page never takes a stale one).
 */
export const takeHtmlSnapshot = (
  workspaceId: string,
  projectId: string,
): { readonly at: number; readonly response: Promise<Response> } | null => {
  const b = boot()
  const early = b?.snapshot
  if (!b || !early) return null
  delete b.snapshot
  if (early.workspaceId !== workspaceId || early.projectId !== projectId) return null
  return { at: b.at, response: early.response }
}

/**
 * A `fetch` that answers with an already sent request's response, and still honours the caller's abort signal (so the
 * session's and the list's 10 s `RequestTimeout` keeps working when the early request hangs).
 */
export const fetchFrom =
  (response: Promise<Response>): typeof fetch =>
  (_input, init) =>
    new Promise<Response>((resolve, reject) => {
      const signal = init?.signal
      if (signal?.aborted) {
        reject(signal.reason)
        return
      }
      signal?.addEventListener("abort", () => reject(signal.reason), { once: true })
      response.then(resolve, reject)
    })
